import React, { useState } from 'react'
import { formatInTimeZone } from 'date-fns-tz'
import { Copy, Search } from 'lucide-react'
import styled from 'styled-components'
import { Flex, Text } from '@workwhile/ui'

export type CompanyEntry = {
  id: number
  name: string
  createdAt?: string
  type: 'w2' | '1099'
  historyType: 'companies'
}

export type CompanyGroupEntry = {
  id: number
  name: string
  createdAt?: string
  companyCount: number
  historyType: 'company_groups'
}

export type UserEntry = {
  id: string | number
  name?: string
  email?: string
  historyType: 'users'
}

export type ShiftEntry = {
  id: string | number
  startsAt?: string
  historyType: 'shifts'
}

export type WorkItemEntry = {
  id: string | number
  workerId: number
  shiftId: number
  status: string
  startedAt?: string
  completedAt?: string
  historyType: 'work_items'
}

export type PaymentEntry = {
  id: string | number
  status?: string
  amount?: number
  historyType: 'payments'
}

export type PositionEntry = {
  id: number
  name: string
  companyName: string
  historyType: 'positions'
}

export type RouteEntry = {
  id: string
  path: string
  label: string
  historyType: 'routes'
}

export type CommandPaletteOption =
  | CompanyEntry
  | CompanyGroupEntry
  | UserEntry
  | ShiftEntry
  | WorkItemEntry
  | PaymentEntry
  | PositionEntry
  | RouteEntry

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  font-size: 14px;
`

export const DetailsTitle = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  color: #111827;
  margin-bottom: 8px;
`

export const DetailsDescription = styled.div`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.5;
`

export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleDateString()
  } catch {
    return dateString
  }
}

const TIME_FORMAT = "yyyy-MM-dd' 'h:mm a' 'z"

export const formatDateTime = (
  dateString: string | undefined,
  timeZone: string
): string => {
  if (!dateString) return 'N/A'
  try {
    return formatInTimeZone(dateString, timeZone, TIME_FORMAT)
  } catch {
    return dateString
  }
}

export const getValue = (
  value: string | number | boolean | undefined | null,
  defaultValue = 'N/A'
) => {
  if (value === undefined || value === null) return defaultValue
  return String(value)
}

export function CopyButton({
  value,
  fieldName,
  item,
  onItemInteraction,
}: {
  value: string | number
  fieldName: string
  item: CommandPaletteOption
  onItemInteraction: (item: CommandPaletteOption) => void
}) {
  const [isCopied, setIsCopied] = useState(false)

  if (value === 'N/A') return null

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value))
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
      if (item && onItemInteraction) {
        onItemInteraction(item)
      }
    } catch (err) {
      //
    }
  }

  return (
    <button
      onClick={handleCopy}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '2px 6px',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        backgroundColor: isCopied ? '#10b981' : '#ffffff',
        color: isCopied ? '#ffffff' : '#6b7280',
        cursor: 'pointer',
        fontSize: '11px',
        transition: 'all 0.2s',
      }}
      title={`Copy ${fieldName}`}
    >
      <Copy size={12} />
    </button>
  )
}

export function CopyableValue({
  value,
  fieldName,
  item,
  onItemInteraction,
}: {
  value: string | number
  fieldName: string
  item: CommandPaletteOption
  onItemInteraction: (item: CommandPaletteOption) => void
}) {
  if (value === 'N/A') return <Text as="span">{value}</Text>

  return (
    <Flex alignItems="center" justifyContent="flex-end" gap={1} width="100%">
      <Text as="span">{value}</Text>
      <CopyButton
        value={value}
        fieldName={fieldName}
        item={item}
        onItemInteraction={onItemInteraction}
      />
    </Flex>
  )
}

export function SectionTitleWithButton({
  title,
  buttonText,
  onButtonClick,
  buttonTitle,
}: {
  title: string
  buttonText: string
  onButtonClick: () => void
  buttonTitle?: string
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
      }}
    >
      <Text fontSize={2} fontWeight={500} color="text">
        {title}
      </Text>
      <button
        onClick={onButtonClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: '#ffffff',
          color: '#6b7280',
          cursor: 'pointer',
          fontSize: '11px',
          transition: 'all 0.2s',
        }}
        title={buttonTitle}
      >
        <Search size={12} style={{ marginRight: '4px' }} />
        {buttonText}
      </button>
    </div>
  )
}

// based on provided query,returns an array of text segments with highlight information
export const highlightText = (
  text: string,
  query: string
): Array<{ text: string; highlight: boolean }> => {
  if (!query.trim() || !text) return [{ text, highlight: false }]

  const normalizedText = text.toLowerCase()
  const normalizedQuery = query.toLowerCase().trim()

  const index = normalizedText.indexOf(normalizedQuery)

  // exact match not found (company fuzzy search), try to find the longest matching substring
  if (index === -1) {
    let bestMatch = { start: -1, length: 0 }

    for (let i = 0; i < normalizedText.length; i++) {
      for (let j = i + 1; j <= normalizedText.length; j++) {
        const substring = normalizedText.substring(i, j)
        if (
          normalizedQuery.includes(substring) &&
          substring.length > bestMatch.length
        ) {
          bestMatch = { start: i, length: substring.length }
        }
      }
    }

    if (bestMatch.start !== -1) {
      const before = text.substring(0, bestMatch.start)
      const match = text.substring(
        bestMatch.start,
        bestMatch.start + bestMatch.length
      )
      const after = text.substring(bestMatch.start + bestMatch.length)

      return [
        { text: before, highlight: false },
        { text: match, highlight: true },
        { text: after, highlight: false },
      ].filter((segment) => segment.text.length > 0)
    }

    return [{ text, highlight: false }]
  }

  // exact match found
  const before = text.substring(0, index)
  const match = text.substring(index, index + normalizedQuery.length)
  const after = text.substring(index + normalizedQuery.length)

  return [
    { text: before, highlight: false },
    { text: match, highlight: true },
    { text: after, highlight: false },
  ].filter((segment) => segment.text.length > 0)
}

export function HighlightedText({
  text,
  query,
}: {
  text: string
  query: string
}) {
  const segments = highlightText(text, query)

  return (
    <>
      {segments.map((segment, index) =>
        segment.highlight ? (
          <span
            key={index}
            style={{
              backgroundColor: '#fef3c7',
              fontWeight: 600,
              color: '#92400e',
            }}
          >
            {segment.text}
          </span>
        ) : (
          <span key={index}>{segment.text}</span>
        )
      )}
    </>
  )
}
